import { createColumnHelper } from '@tanstack/react-table';
import { AssignedCashierInfo, useRepositoryQuery } from '@mews-commander/data-access';
import { EnterpriseLink } from '@mews-commander/ui';
import { compactArray } from '@mews/fp';
import { lt } from '@mews/framework';
import { Table, TableProvider, Tag, useTableState } from '@optimus-web/core';
import { SlicedItemListWithBadge } from '../../../common/components/SlicedItemListWithBadge';

const helper = createColumnHelper<AssignedCashierInfo>();

const tableColumns = [
    helper.accessor('name', {
        header: () => lt('Name'),
        cell: ({ getValue, row: { original } }) => (
            <EnterpriseLink
                variant="basic"
                to="Finance/Cashier/$cashierId/Detail"
                params={{
                    cashierId: original.id,
                }}
            >
                {getValue()}
            </EnterpriseLink>
        ),
    }),
    helper.accessor('enabledCurrencies', {
        header: () => lt('EnabledCurrencies'),
        cell: ({ getValue }) => (
            <SlicedItemListWithBadge
                items={compactArray(getValue().map((it) => it.code))}
                cutoff={4}
                lastMaxLength={2}
            />
        ),
    }),
    helper.accessor('isEnabled', {
        header: () => lt('Status'),
        cell: ({ getValue }) =>
            getValue() ? (
                <Tag variant="success">{lt('Enabled')}</Tag>
            ) : (
                <Tag variant="basicSubtle">{lt('Disabled')}</Tag>
            ),
    }),
];

export const CashierShiftsTable = () => {
    const [tableState, setTableState] = useTableState<AssignedCashierInfo>({
        pagination: {
            show: false,
        },
    });

    const { data, isLoading } = useRepositoryQuery('cashiers', 'getAssigned', {});

    const cashiers = data?.cashiers ?? [];

    return (
        <TableProvider columns={tableColumns} state={tableState} onStateChange={setTableState}>
            <Table
                data-test-id="cashier-shifts-table"
                loading={isLoading}
                uniqueRowId="id"
                data={cashiers}
                amountOfRows={cashiers.length}
            />
        </TableProvider>
    );
};
